import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Button, TextField, InputAdornment, Tooltip,
  Avatar, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { BRAND } from '../theme/tokens';
import { StatusChip } from '../components/StatusChip';

/**
 * Real, selectable jobs (used when the bill has a Job but no PO).
 * Always shown as quick-pick chips so the user can attach a bill to a real
 * job in one click instead of typing a number.
 */
const KNOWN_JOBS: { ref: string; label: string; client: string }[] = [
  { ref: '24',   label: 'Job #24',   client: 'Coastline Civil — Bridge Pier Repairs' },
  { ref: '187',  label: 'Job #187',  client: 'Yandina Council — Roadside Mowing' },
  { ref: '203',  label: 'Job #203',  client: 'Northbridge Workshop — Internal' },
];

/**
 * Real projects (used when the bill has a Project but no PO/Job).
 */
const KNOWN_PROJECTS: { ref: string; label: string }[] = [
  { ref: 'PRJ-2026-04', label: 'Sunshine Coast Plant Maintenance' },
  { ref: 'PRJ-2026-07', label: 'Dump-Truck Rebuild Programme' },
  { ref: 'PRJ-2026-09', label: 'Workshop Fit-out' },
];

type ImportMode = 'po' | 'job-no-po' | 'project-no-po' | 'none' | null;

const fmt = (n: number) => `$${n.toFixed(2)}`;

export const BillsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // v1.14 — Statements page links here with ?status=Pending+Goods+Receipt to
  // surface the IRNG queue. Other status values are accepted equivalently.
  const statusFilter = searchParams.get('status');
  const { bills: BILLS, suppliers: SUPPLIERS, purchaseOrders: PURCHASE_ORDERS, createBill, updateBill } = useData();
  const [search, setSearch] = useState('');

  // ── Import-mode picker (the 4-option entry dialog) ───────────────────────
  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const [importMode, setImportMode] = useState<ImportMode>(null);

  // ── New bill dialog ─────────────────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newSupplier, setNewSupplier] = useState('');
  const [newLinkedPo, setNewLinkedPo] = useState('');
  const [newJobRef, setNewJobRef] = useState('');
  const [newProjectRef, setNewProjectRef] = useState('');
  const [created, setCreated] = useState(false);

  const resetDialog = () => {
    setNewSupplier('');
    setNewLinkedPo('');
    setNewJobRef('');
    setNewProjectRef('');
    setCreated(false);
  };

  const openDialog = (mode: ImportMode = 'po') => {
    resetDialog();
    setImportMode(mode);
    setDialogOpen(true);
  };

  const handleModePick = (mode: Exclude<ImportMode, null>) => {
    setModeDialogOpen(false);
    if (mode === 'po') {
      // v1.13 — every imported invoice now has to belong to a Statement, so
      // the "I have a PO" upload path lands on /statements (pick or create a
      // statement first; uploads then auto-link to that statement's queue).
      navigate('/statements');
      return;
    }
    openDialog(mode);
  };

  const handlePoSelect = (poId: string) => {
    setNewLinkedPo(poId);
    const po = PURCHASE_ORDERS.find(p => p.id === poId);
    if (po?.jobRef) setNewJobRef(po.jobRef.replace('JOB #', ''));
    if (po?.supplierId && !newSupplier) setNewSupplier(po.supplierId);
  };

  const filteredPos = PURCHASE_ORDERS.filter(po =>
    !newSupplier || po.supplierId === newSupplier
  );

  const nextBillNo = `BILL-2026-${String(BILLS.length + 1).padStart(3, '0')}`;

  const handleCreate = () => {
    const jobRef = newJobRef.trim()
      ? (newJobRef.trim().toUpperCase().startsWith('JOB')
          ? newJobRef.trim()
          : `JOB #${newJobRef.trim().replace(/[^0-9]/g, '')}`)
      : '';
    const id = createBill({
      supplierId: newSupplier,
      linkedPoId: importMode === 'job-no-po' || importMode === 'project-no-po' || importMode === 'none'
        ? null
        : (newLinkedPo || null),
      jobRef,
    });
    if (newProjectRef.trim()) {
      // Stash project ref in the bill `reference` field for now (Bill type has
      // no dedicated project field — reference is free-form text).
      updateBill(id, { reference: `PROJECT: ${newProjectRef.trim()}` });
    }
    setCreated(true);
    setTimeout(() => {
      setDialogOpen(false);
      navigate(`/bills/${id}`);
    }, 900);
  };

  const filtered = BILLS.filter(b => {
    if (statusFilter && b.status !== statusFilter) return false;
    const sup = SUPPLIERS.find(s => s.id === b.supplierId);
    const q = search.toLowerCase();
    return (
      b.billNo.toLowerCase().includes(q) ||
      b.supplierInvoiceNo.toLowerCase().includes(q) ||
      b.jobRef.toLowerCase().includes(q) ||
      (b.linkedPoId || '').toLowerCase().includes(q) ||
      (sup?.name || '').toLowerCase().includes(q)
    );
  });

  const totalValue = BILLS.reduce((s, b) => s + b.totalIncGst, 0);
  const pending    = BILLS.filter(b => b.status === 'Draft' || b.status === 'Awaiting approval' || b.status === 'Approved').length;
  const paid       = BILLS.filter(b => b.status === 'Paid').length;
  const awaiting   = BILLS.filter(b => b.status === 'Awaiting approval').length;

  const summaryCards = [
    { label: 'Total Bills',  value: BILLS.length,         color: BRAND, sub: 'all time' },
    { label: 'Pending',      value: pending,               color: '#027BFF', sub: 'to approve / pay' },
    { label: 'Paid',         value: paid,                  color: '#2e7d32', sub: 'this month' },
    { label: 'Awaiting approval', value: awaiting,         color: '#e65100', sub: 'in workflow' },
    { label: 'Total Value',  value: fmt(totalValue),       color: '#5d4037', sub: 'inc. GST' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReceiptLongIcon sx={{ color: BRAND, fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', lineHeight: 1.2 }}>
              Bills
            </Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Supplier invoices — upload PDFs and process line items
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Drop a supplier invoice. Alfred will ask whether you have a PO, a Job, a Project, or none — then route it accordingly." arrow>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700 }}
              onClick={() => setModeDialogOpen(true)}
            >
              Import Bill
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, color: BRAND, borderColor: BRAND }}
            onClick={() => navigate('/bills/new')}
          >
            New Bill
          </Button>
        </Stack>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.map(c => (
          <Paper key={c.label} elevation={0} sx={{
            flex: '1 1 140px', p: 1.5, border: '1px solid #e8e0f5',
            borderTop: `3px solid ${c.color}`, borderRadius: 1,
          }}>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: c.color, lineHeight: 1 }}>
              {c.value}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#555', mt: 0.3 }}>{c.label}</Typography>
            <Typography sx={{ fontSize: '0.65rem', color: '#aaa' }}>{c.sub}</Typography>
          </Paper>
        ))}
      </Box>

      {/*
        ── Active status filter pill (v1.14) ────────────────────────────────────
        When the user arrives here with ?status=… (typically from the Statements
        page action-required widget), show a clearly-labelled chip so they know
        they're looking at a filtered subset, with one click to clear.
      */}
      {statusFilter && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Filtered: ${statusFilter} (${filtered.length})`}
            onDelete={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('status');
              setSearchParams(next, { replace: true });
            }}
            sx={{ fontSize: '0.72rem', fontWeight: 700, backgroundColor: '#fff3e0',
              color: '#e65100', border: '1px solid #ffcc80' }}
          />
        </Box>
      )}

      {/* Search */}
      <Box sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search bills, supplier, invoice #, job…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 340 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#aaa' }} /></InputAdornment>,
          }}
        />
      </Box>

      {/* ── Import-Bill Mode Picker Dialog ─────────────────────────────────
            Asks the user up-front: do you have a PO, just a Job, just a
            Project, or nothing at all? This routes the import to the
            cleanest workflow for that scenario.                            */}
      <Dialog open={modeDialogOpen} onClose={() => setModeDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon sx={{ color: BRAND }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Import a Supplier Bill</Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={() => setModeDialogOpen(false)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          <Typography sx={{ fontSize: '0.78rem', color: '#666', mt: 0.4 }}>
            Before we drop the file — what does this invoice relate to? Pick one to get the right workflow.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {/* ① PO */}
            <Paper elevation={0} onClick={() => handleModePick('po')}
              sx={{ p: 2, cursor: 'pointer', border: '2px solid #e0e0e0', borderRadius: 2,
                transition: 'all 0.15s', '&:hover': { borderColor: BRAND, backgroundColor: '#f8f5ff', boxShadow: '0 4px 12px rgba(103,78,167,0.15)' } }}>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <Box sx={{ width: 40, height: 40, borderRadius: 1.5, backgroundColor: '#e8f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <LinkIcon sx={{ color: '#027BFF', fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#222', mb: 0.4 }}>I have a PO</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#666', lineHeight: 1.5 }}>
                    The supplier already has a PO from us. Match invoice lines to PO lines, reconcile prices and quantities.
                  </Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: BRAND, fontWeight: 700, mt: 0.6 }}>
                    → Pick a Statement to reconcile (or start a new one)
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* ② Job, no PO */}
            <Paper elevation={0} onClick={() => handleModePick('job-no-po')}
              sx={{ p: 2, cursor: 'pointer', border: '2px solid #e0e0e0', borderRadius: 2,
                transition: 'all 0.15s', '&:hover': { borderColor: '#f57c00', backgroundColor: '#fff8e1', boxShadow: '0 4px 12px rgba(245,124,0,0.18)' } }}>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <Box sx={{ width: 40, height: 40, borderRadius: 1.5, backgroundColor: '#fff3d6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <WorkIcon sx={{ color: '#f57c00', fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#222', mb: 0.4 }}>I have a Job (no PO)</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#666', lineHeight: 1.5 }}>
                    Costs against a real job — e.g. <strong>Job #24</strong> — but no PO was raised. Bill is dropped straight onto the job.
                  </Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: '#f57c00', fontWeight: 700, mt: 0.6 }}>
                    → New bill linked to the job
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* ③ Project, no PO */}
            <Paper elevation={0} onClick={() => handleModePick('project-no-po')}
              sx={{ p: 2, cursor: 'pointer', border: '2px solid #e0e0e0', borderRadius: 2,
                transition: 'all 0.15s', '&:hover': { borderColor: '#7e57c2', backgroundColor: '#f3eefa', boxShadow: '0 4px 12px rgba(126,87,194,0.2)' } }}>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <Box sx={{ width: 40, height: 40, borderRadius: 1.5, backgroundColor: '#ede4f7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FolderSpecialIcon sx={{ color: '#7e57c2', fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#222', mb: 0.4 }}>I have a Project (no PO)</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#666', lineHeight: 1.5 }}>
                    Multi-job programme of work, no specific job or PO. Costs are pooled at the project level for later allocation.
                  </Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: '#7e57c2', fontWeight: 700, mt: 0.6 }}>
                    → New bill linked to the project
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* ④ None */}
            <Paper elevation={0} onClick={() => handleModePick('none')}
              sx={{ p: 2, cursor: 'pointer', border: '2px solid #e0e0e0', borderRadius: 2,
                transition: 'all 0.15s', '&:hover': { borderColor: '#666', backgroundColor: '#f5f5f5', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <Box sx={{ width: 40, height: 40, borderRadius: 1.5, backgroundColor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AssignmentIcon sx={{ color: '#666', fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#222', mb: 0.4 }}>No Job, No PO</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#666', lineHeight: 1.5 }}>
                    General overhead expense — fuel card, phone bill, office supplies. Goes to the GL with no job allocation.
                  </Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: '#666', fontWeight: 700, mt: 0.6 }}>
                    → New bill, no link
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* Help row */}
          <Box sx={{ mt: 2, p: 1.2, backgroundColor: '#f8f5ff', borderRadius: 1.5, border: `1px solid ${BRAND}22`, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <HelpOutlineIcon sx={{ fontSize: 16, color: BRAND, mt: 0.2 }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#555', lineHeight: 1.6 }}>
              <strong>Not sure?</strong> Pick "I have a PO" if any number on the supplier invoice looks like a PO ref (e.g. <code>PO-1648</code>).
              Otherwise pick the most specific level — Job → Project → None.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── New Bill Dialog ──────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => !created && setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon sx={{ color: BRAND }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
              {importMode === 'job-no-po'    ? 'New Bill — Job, no PO' :
               importMode === 'project-no-po' ? 'New Bill — Project, no PO' :
               importMode === 'none'          ? 'New Bill — no Job, no PO' :
                                                'New Supplier Bill'}
            </Typography>
            {importMode && importMode !== 'po' && (
              <Button size="small" onClick={() => { setDialogOpen(false); setModeDialogOpen(true); }}
                sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.7rem', color: '#888' }}>
                Change mode
              </Button>
            )}
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: '#888', mt: 0.3 }}>
            Creates an empty draft bill — then import the supplier invoice PDF
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {/* How it works */}
          <Box sx={{ p: 1.5, mb: 2, backgroundColor: '#f8f5ff', borderRadius: 2, border: `1px solid ${BRAND}33` }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#555', lineHeight: 1.6 }}>
              Creates <strong>{nextBillNo}</strong> as a blank draft.
              You'll then land on the bill and can drag in the supplier invoice PDF to extract line items automatically.
            </Typography>
          </Box>

          {/* Supplier */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Supplier *</InputLabel>
            <Select value={newSupplier} label="Supplier *" onChange={e => { setNewSupplier(e.target.value); setNewLinkedPo(''); }}>
              {SUPPLIERS.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 22, height: 22, fontSize: '0.58rem', backgroundColor: BRAND }}>{s.code.slice(0, 2)}</Avatar>
                    <Typography sx={{ fontSize: '0.82rem' }}>{s.name}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Link to PO — only for the PO mode */}
          {importMode === 'po' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Link to PO (optional)</InputLabel>
              <Select value={newLinkedPo} label="Link to PO (optional)" onChange={e => handlePoSelect(e.target.value)}>
                <MenuItem value=""><Typography sx={{ fontSize: '0.8rem', color: '#aaa' }}>No PO link</Typography></MenuItem>
                {filteredPos.map(po => {
                  const sup = SUPPLIERS.find(s => s.id === po.supplierId);
                  return (
                    <MenuItem key={po.id} value={po.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: BRAND }}>{po.id}</Typography>
                        {po.lines === 0 && (
                          <Chip label="0 items" size="small" sx={{ fontSize: '0.6rem', height: 16, backgroundColor: '#fff8e1', color: '#f57c00', fontWeight: 700 }} />
                        )}
                        <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>· {sup?.name} · {po.jobRef || '—'}</Typography>
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {/* Job Reference — for PO and Job-no-PO modes */}
          {(importMode === 'po' || importMode === 'job-no-po') && (
            <Box sx={{ mb: 2 }}>
              {importMode === 'job-no-po' && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#666', mb: 0.6, fontWeight: 600 }}>
                    Quick-pick a real job:
                  </Typography>
                  <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                    {KNOWN_JOBS.map(j => {
                      const selected = newJobRef.replace(/[^0-9]/g, '') === j.ref;
                      return (
                        <Tooltip key={j.ref} title={j.client} arrow>
                          <Chip
                            icon={<WorkIcon sx={{ fontSize: 12 }} />}
                            label={j.label}
                            size="small"
                            onClick={() => setNewJobRef(j.ref)}
                            sx={{ fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
                              backgroundColor: selected ? '#f57c00' : '#fff8e1',
                              color: selected ? '#fff' : '#f57c00',
                              border: `1px solid ${selected ? '#f57c00' : '#ffd699'}`,
                              '&:hover': { backgroundColor: selected ? '#ef6c00' : '#ffe7a3' } }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Stack>
                </Box>
              )}
              <TextField
                fullWidth size="small"
                label={importMode === 'job-no-po' ? 'Job Reference *' : 'Job Reference (optional)'}
                placeholder="e.g. 24"
                value={newJobRef}
                onChange={e => setNewJobRef(e.target.value)}
                helperText={newJobRef.trim() ? `Will save as JOB #${newJobRef.trim().replace(/[^0-9]/g, '')}` : (importMode === 'po' ? 'Auto-fills from linked PO' : 'Type a job number or pick one above')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkIcon sx={{ fontSize: 15, color: '#aaa' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#aaa', ml: 0.3 }}>JOB #</Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {/* Project Reference — only for Project-no-PO mode */}
          {importMode === 'project-no-po' && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#666', mb: 0.6, fontWeight: 600 }}>
                Quick-pick a project:
              </Typography>
              <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {KNOWN_PROJECTS.map(p => {
                  const selected = newProjectRef === p.ref;
                  return (
                    <Tooltip key={p.ref} title={p.label} arrow>
                      <Chip
                        icon={<FolderSpecialIcon sx={{ fontSize: 12 }} />}
                        label={p.ref}
                        size="small"
                        onClick={() => setNewProjectRef(p.ref)}
                        sx={{ fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
                          backgroundColor: selected ? '#7e57c2' : '#f3eefa',
                          color: selected ? '#fff' : '#7e57c2',
                          border: `1px solid ${selected ? '#7e57c2' : '#d6c4ec'}`,
                          '&:hover': { backgroundColor: selected ? '#6a46b0' : '#e6d8f5' } }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
              <TextField
                fullWidth size="small" label="Project Reference *"
                placeholder="e.g. PRJ-2026-04"
                value={newProjectRef}
                onChange={e => setNewProjectRef(e.target.value)}
                helperText="Will save on the bill as PROJECT: <ref>"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FolderSpecialIcon sx={{ fontSize: 15, color: '#aaa' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {/* None mode — informational only */}
          {importMode === 'none' && (
            <Box sx={{ p: 1.5, mb: 1, backgroundColor: '#fafafa', borderRadius: 2, border: '1px dashed #ccc' }}>
              <Typography sx={{ fontSize: '0.74rem', color: '#666', lineHeight: 1.55 }}>
                <strong>Overhead bill</strong> — no job or project allocation. Use this for general
                expenses like fuel cards, phone bills, or office supplies. The bill will go straight
                to the GL.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#888' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !newSupplier ||
              created ||
              (importMode === 'job-no-po'     && !newJobRef.trim()) ||
              (importMode === 'project-no-po' && !newProjectRef.trim())
            }
            onClick={handleCreate}
            startIcon={created ? <CheckCircleIcon /> : <CloudUploadIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: '#FF6A13',
              '&:hover': { backgroundColor: '#F35627' } }}
          >
            {created ? `${nextBillNo} created — opening…` : `Create ${nextBillNo}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
              {['Bill #', 'Date', 'Supplier', 'Supplier Inv #', 'Linked PO', 'Job', 'Ex GST', 'GST', 'Total', 'Status', ''].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: BRAND, py: 1 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((bill, idx) => {
              const sup = SUPPLIERS.find(s => s.id === bill.supplierId);
              return (
                <TableRow
                  key={bill.id}
                  hover
                  sx={{ cursor: 'pointer', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}
                  onClick={() => navigate(`/bills/${bill.id}`)}
                >
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: BRAND }}>
                    {bill.billNo}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: '#555' }}>{bill.date}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Avatar sx={{ width: 22, height: 22, fontSize: '0.6rem', backgroundColor: BRAND }}>
                        {sup?.code.slice(0, 2)}
                      </Avatar>
                      <Typography sx={{ fontSize: '0.75rem' }}>{sup?.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.72rem', color: '#666' }}>{bill.supplierInvoiceNo}</TableCell>
                  <TableCell>
                    {bill.linkedPoId ? (
                      <Tooltip title="View linked PO">
                        <Chip
                          icon={<LinkIcon sx={{ fontSize: 12 }} />}
                          label={bill.linkedPoId}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', color: '#027BFF', borderColor: '#027BFF', height: 20, cursor: 'pointer' }}
                          onClick={e => { e.stopPropagation(); navigate('/purchase-orders-list'); }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: '0.72rem', color: '#bbb' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {bill.jobRef ? (
                      <Chip label={bill.jobRef} size="small"
                        sx={{ fontSize: '0.65rem', backgroundColor: '#fff8e1', color: '#f57c00', fontWeight: 600, height: 20 }} />
                    ) : (
                      <Typography sx={{ fontSize: '0.72rem', color: '#bbb' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right' }}>{fmt(bill.subtotalExGst)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right', color: '#888' }}>{fmt(bill.totalGst)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, textAlign: 'right' }}>{fmt(bill.totalIncGst)}</TableCell>
                  <TableCell>
                    <StatusChip status={bill.status} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="View Bill">
                        <Button size="small" variant="outlined"
                          startIcon={<VisibilityIcon sx={{ fontSize: 12 }} />}
                          sx={{ textTransform: 'none', fontSize: '0.65rem', py: 0.2, borderColor: BRAND, color: BRAND, minWidth: 'unset' }}
                          onClick={e => { e.stopPropagation(); navigate(`/bills/${bill.id}`); }}
                        >
                          View
                        </Button>
                      </Tooltip>
                      {!bill.pdfUploaded && (
                        <Tooltip title="Upload & Process PDF">
                          <Button size="small" variant="contained"
                            startIcon={<UploadFileIcon sx={{ fontSize: 12 }} />}
                            sx={{ textTransform: 'none', fontSize: '0.65rem', py: 0.2, backgroundColor: '#f57c00', minWidth: 'unset' }}
                            onClick={e => { e.stopPropagation(); navigate(`/bills/${bill.id}`); }}
                          >
                            Upload
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
